import type { ReactNode, RefObject } from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

const sectionIds = [
  'mission',
  'vision',
  'product',
  'interview',
  'member',
  'entries',
  'skills',
  'git-hub',
  'basic-information',
  'summary',
  'links',
  'press-release',
  'events',
] as const;

export type SectionIds = (typeof sectionIds)[number];

type SectionScrollObserverContext = {
  activeSection: SectionIds | null;
  sectionRefs: RefObject<Record<SectionIds, HTMLElement | null>>;
};

const SectionScrollContext = createContext<
  SectionScrollObserverContext | undefined
>(undefined);

export const useSectionScrollObserverContext =
  (): SectionScrollObserverContext => {
    const context = useContext(SectionScrollContext);

    if (!context) {
      throw new Error(
        'useSectionScrollObserverContext must be used within a SectionScrollObserverProvider'
      );
    }

    return context;
  };

const useSectionScrollObserver = () => {
  const [activeSection, setActiveSection] = useState<SectionIds | null>(null);
  const sectionRefs = useRef<Record<SectionIds, HTMLElement | null>>(
    Object.fromEntries(sectionIds.map((id) => [id, null])) as Record<
      SectionIds,
      null
    >
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id as SectionIds);
          } else {
            setActiveSection(null);
          }
        });
      },
      {
        root: null,
        rootMargin: '-10% 0px -90% 0px', // 画面上部10%の位置で検知
        threshold: 0, // 1pxでも表示されていれば検知
      }
    );

    // 各セクションを監視
    Object.values(sectionRefs.current).forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return { activeSection, sectionRefs };
};

export const SectionScrollObserverProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const observer = useSectionScrollObserver();

  return (
    <SectionScrollContext.Provider value={observer}>
      {children}
    </SectionScrollContext.Provider>
  );
};
